import type { CertificationPreview } from "@brookbush/types";
import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { ArrowUpRightIcon, DownloadIcon } from "lucide-react";
import { useRouter } from "next/compat/router";
import { useMemo } from "react";
import { FaCheckCircle } from "react-icons/fa";

import { useAccess, usePassedCertifications } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";
import {
	getCertificateUnlockHref,
	getCertificateUnlockLabel,
} from "~/lib/certificateUnlockCta";

interface CertificationPassedCardProps {
	certification: CertificationPreview;
}

const CertificationPassedCard = ({
	certification,
}: CertificationPassedCardProps) => {
	const { passedCertifications } = usePassedCertifications();
	const router = useRouter();

	const { isMember, isTrialing } = useAccess();
	const trackCTA = useTrackCTA();

	const { actor } = useAuth();
	const passedCertification = useMemo(
		() =>
			passedCertifications?.find(
				(passedCertification) =>
					passedCertification.certificationId === certification._id,
			),
		[certification._id, passedCertifications],
	);

	const hasAccess = (isMember && !isTrialing) || actor;

	if (!passedCertification) return null;

	const baseCertificateUrl = `${process.env.NEXT_PUBLIC_API_URL}/certifications/passed/${passedCertification.id}/certificate`;

	const unlockCertificate = () => {
		trackCTA(UserAction.BECOME_A_MEMBER, "Unlock Passed Certification Card");
		router?.push(getCertificateUnlockHref("passed_certification_card"));
	};

	return (
		<div className="mx-auto flex w-full max-w-lg justify-between overflow-hidden rounded-xl border border-emerald-800/10 bg-emerald-50 phablet:px-4 px-3 phablet:py-2 py-2 dark:border-emerald-300/20 dark:bg-emerald-300/20">
			<p className="flex items-center gap-2 whitespace-nowrap font-medium text-lg">
				<FaCheckCircle className={"text-emerald-500"} /> Passed
			</p>

			<div className="flex gap-4">
				{hasAccess ? (
					<>
						<button
							onClick={() => {
								window.open(baseCertificateUrl);
							}}
							className="group flex items-center gap-1 whitespace-nowrap font-medium hover:underline disabled:pointer-events-none disabled:opacity-75"
						>
							{getCertificateUnlockLabel({
								hasAccess: true,
								action: "view",
							})}{" "}
							<ArrowUpRightIcon
								size={18}
								strokeWidth={2.25}
								className="transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
							/>
						</button>

						<button
							onClick={() => {
								window.open(`${baseCertificateUrl}?download=true`);
							}}
							className="group flex items-center gap-1.5 whitespace-nowrap font-medium hover:underline disabled:pointer-events-none disabled:opacity-75"
						>
							{getCertificateUnlockLabel({
								hasAccess: true,
								action: "download",
							})}{" "}
							<DownloadIcon
								size={18}
								strokeWidth={2.25}
								className="transition-all group-hover:translate-y-0.5"
							/>
						</button>
					</>
				) : (
					<button
						onClick={unlockCertificate}
						className="group flex items-center gap-1 whitespace-nowrap font-medium hover:underline"
					>
						{getCertificateUnlockLabel({
							hasAccess: false,
							action: "view",
						})}
						<ArrowUpRightIcon
							size={18}
							strokeWidth={2.25}
							className="transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
						/>
					</button>
				)}
			</div>
		</div>
	);
};

export default CertificationPassedCard;
